/**
 * Oma Organics — Wishlist Klaviyo Event Bridge
 *
 * Listens for custom wishlist events dispatched by the wishlist snippet/page
 * and forwards them to Klaviyo with full product metadata.
 *
 * Load this script on every page (e.g. via theme.liquid or a script tag).
 *
 * Events handled:
 *   oma:wishlist:add      -> Klaviyo "wishlist_item_added"
 *   oma:wishlist:remove   -> Klaviyo "wishlist_item_removed"
 *   oma:wishlist:shared   -> Klaviyo "wishlist_shared"
 */
(function () {
  'use strict';

  var COOKIE = 'oma_wishlist';

  /* ---- Cookie reader ---- */
  function getWishlist() {
    try {
      var raw = document.cookie.split('; ').find(function (c) {
        return c.indexOf(COOKIE + '=') === 0;
      });
      if (!raw) return [];
      return JSON.parse(decodeURIComponent(raw.split('=').slice(1).join('=')));
    } catch (e) {
      return [];
    }
  }

  /* ---- Klaviyo push helper ---- */
  var eventQueue = [];
  var profileQueue = [];

  function klaviyoReady() {
    return !!(window._learnq && typeof window._learnq.push === 'function');
  }

  function pushEvent(name, data) {
    if (klaviyoReady()) {
      window._learnq.push(['track', name, data]);
    } else {
      eventQueue.push({ name: name, data: data });
    }
  }

  function pushProfile(props) {
    if (klaviyoReady()) {
      window._learnq.push(['identify', props]);
    } else {
      profileQueue.push(props);
    }
  }

  /* Flush queued events once Klaviyo appears */
  function flushQueue() {
    if (!klaviyoReady()) return;

    while (eventQueue.length) {
      var ev = eventQueue.shift();
      window._learnq.push(['track', ev.name, ev.data]);
    }
    while (profileQueue.length) {
      window._learnq.push(['identify', profileQueue.shift()]);
    }
  }

  /* Poll for Klaviyo if not yet loaded (checks every 500ms, stops after 30s) */
  var pollCount = 0;
  var pollInterval = setInterval(function () {
    pollCount++;
    if (klaviyoReady()) {
      flushQueue();
      clearInterval(pollInterval);
    } else if (pollCount > 60) {
      clearInterval(pollInterval);
    }
  }, 500);

  /* ---- Profile property updater ---- */
  function updateProfileProperties() {
    var list = getWishlist();
    var types = list.map(function (item) {
      return (item.type || '').toLowerCase();
    });

    var props = {
      wishlist_item_count: list.length,
      wishlist_contains_perfume: types.some(function (t) {
        return t.indexOf('perfume') > -1 || t.indexOf('fragrance') > -1 || t.indexOf('eau') > -1;
      }),
      wishlist_contains_face_oil: types.some(function (t) {
        return t.indexOf('face oil') > -1 || t.indexOf('facial oil') > -1;
      }),
      wishlist_last_updated: new Date().toISOString()
    };

    pushProfile(props);
  }

  /* ---- Build Klaviyo payload from event detail ---- */
  function buildProductPayload(detail) {
    return {
      ProductID: detail.id,
      ProductHandle: detail.handle,
      ProductTitle: detail.title,
      ProductPrice: typeof detail.price === 'number' ? (detail.price / 100).toFixed(2) : detail.price,
      ProductType: detail.type || '',
      VariantID: detail.variant_id || '',
      ScentFamily: detail.scent_family || '',
      ProductURL: detail.url || (window.location.origin + '/products/' + detail.handle),
      ProductImageURL: detail.image || '',
      AddedAt: detail.added_at || new Date().toISOString(),
      WishlistItemCount: getWishlist().length
    };
  }

  /* ---- Event listeners ---- */

  document.addEventListener('oma:wishlist:add', function (e) {
    var payload = buildProductPayload(e.detail);
    pushEvent('wishlist_item_added', payload);
    updateProfileProperties();
  });

  document.addEventListener('oma:wishlist:remove', function (e) {
    var payload = buildProductPayload(e.detail);
    pushEvent('wishlist_item_removed', payload);
    updateProfileProperties();
  });

  document.addEventListener('oma:wishlist:shared', function (e) {
    var detail = e.detail || {};
    var list = getWishlist();

    var payload = {
      ItemCount: detail.item_count || list.length,
      ShareURL: detail.url || '',
      Items: list.map(function (item) {
        return {
          ProductID: item.id,
          ProductHandle: item.handle,
          ProductTitle: item.title,
          ProductType: item.type || '',
          ScentFamily: item.scent_family || ''
        };
      }),
      SharedAt: new Date().toISOString()
    };

    pushEvent('wishlist_shared', payload);
  });
})();

// === Wishlist Purchase Conversion Tracking ===
// On order confirmation page, check if purchased items were on the wishlist
if (window.Shopify && window.Shopify.checkout) {
  try {
    var wishlistCookie = document.cookie.split(';').find(c => c.trim().startsWith('oma_wishlist='));
    if (wishlistCookie) {
      var wishlist = JSON.parse(decodeURIComponent(wishlistCookie.split('=')[1]));
      var lineItems = window.Shopify.checkout.line_items || [];
      var converted = false;
      lineItems.forEach(function(item) {
        var wasWishlisted = wishlist.some(function(w) { return w.handle === item.handle || w.id === item.product_id; });
        if (wasWishlisted) {
          converted = true;
          var _learnq = window._learnq || [];
          _learnq.push(['track', 'Wishlist Item Purchased', {
            ProductName: item.title,
            ProductID: item.product_id,
            Price: item.price,
            Handle: item.handle
          }]);
        }
      });
      if (converted) {
        var _learnq = window._learnq || [];
        _learnq.push(['identify', { wishlist_to_purchase_converted: true }]);
      }
    }
  } catch(e) {}
}

// === Price Drop on Wishlisted Items ===
// Check wishlisted items against current prices on page load
// If a price dropped, fire an event
try {
  var wishlistCookie2 = document.cookie.split(';').find(c => c.trim().startsWith('oma_wishlist='));
  if (wishlistCookie2) {
    var wishlist2 = JSON.parse(decodeURIComponent(wishlistCookie2.split('=')[1]));
    wishlist2.forEach(function(item) {
      // Fetch current price via Shopify product JSON
      fetch('/products/' + item.handle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var currentPrice = data.product.variants[0].price;
          var savedPrice = parseFloat(item.price) / 100; // wishlist stores in cents
          if (currentPrice < savedPrice && savedPrice > 0) {
            var _learnq = window._learnq || [];
            _learnq.push(['track', 'Price Drop on Wishlisted Item', {
              ProductName: item.title,
              ProductID: item.id,
              Handle: item.handle,
              OldPrice: savedPrice.toFixed(2),
              NewPrice: currentPrice,
              Discount: ((savedPrice - currentPrice) / savedPrice * 100).toFixed(0) + '%'
            }]);
            _learnq.push(['identify', { wishlist_price_drop_detected: true }]);
          }
        })
        .catch(function() {});
    });
  }
} catch(e) {}

// Update wishlist count badge in nav
function updateWishlistBadge() {
  try {
    var cookie = document.cookie.split(';').find(function(c) { return c.trim().startsWith('oma_wishlist='); });
    var count = 0;
    if (cookie) {
      var items = JSON.parse(decodeURIComponent(cookie.split('=')[1]));
      count = items.length;
    }
    var badge = document.getElementById('wishlistCount');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }
  } catch(e) {}
}
updateWishlistBadge();
document.addEventListener('oma:wishlist:add', updateWishlistBadge);
document.addEventListener('oma:wishlist:remove', updateWishlistBadge);
